import {
  AcceptedRestriction,
  FixAction,
  Flag,
  PATH_SEPARATOR,
  StudioDraft,
  levelsForScope,
} from "./studioTypes";
import type { Cdt0Resolution } from "./cdt0Resolution";
import { SIZE_LAYOUT_PREFIX, SIZE_TIER_ATTR, type SizeLayout } from "./sizeTiers";
import type { SizeTierAnswer } from "./studioTypes";

export const reorderSpineLevel = (draft: StudioDraft, from: number, to: number): StudioDraft => {
  if (from === to || from < 0 || to < 0 || from >= draft.spine.length || to >= draft.spine.length) {
    return draft;
  }
  const spine = [...draft.spine];
  // Orientation belongs to tier position, not the attribute that lands there.
  const orientByTier = spine.map((level) => level.orient);
  const [moved] = spine.splice(from, 1);
  spine.splice(to, 0, moved);
  const repatterned = spine.map((level, tier) => ({ ...level, orient: orientByTier[tier] }));
  // Path-keyed maps belong to the old hierarchy; dropping them also puts layout in fresh mode.
  return {
    ...draft,
    spine: repatterned,
    scopedValueOrder: {},
    scopedLevelOrder: {},
    bandOverrides: {},
    orientOverrides: {},
    shelfHints: {},
  };
};

export const reorderSiblings = (
  draft: StudioDraft,
  scopeKey: string,
  order: readonly string[],
): StudioDraft => ({
  ...draft,
  scopedValueOrder: { ...draft.scopedValueOrder, [scopeKey]: order },
});

export const moveSibling = (
  draft: StudioDraft,
  scopeKey: string,
  attr: string,
  value: string,
  offset: number,
  /** Offset is in the drawn sibling list so a step lands on the neighbour the user sees. */
  siblings?: readonly string[],
): StudioDraft => {
  const current = [
    ...(siblings ?? draft.scopedValueOrder[scopeKey] ?? draft.valueOrder[attr] ?? []),
  ];
  const from = current.indexOf(value);
  if (from < 0) return draft;
  const to = from + offset;
  if (to < 0 || to >= current.length) return draft;
  current.splice(from, 1);
  current.splice(to, 0, value);
  return reorderSiblings(draft, scopeKey, current);
};

const applyFixAction = (draft: StudioDraft, action: FixAction): StudioDraft => {
  switch (action.type) {
    case "reorderValues":
      return {
        ...draft,
        scopedValueOrder: { ...draft.scopedValueOrder, [action.scopeKey]: action.order },
      };
    case "retagItems": {
      const attrOverrides = { ...draft.attrOverrides };
      for (const skuId of action.skuIds) {
        attrOverrides[skuId] = { ...attrOverrides[skuId], [action.attr]: action.value };
      }
      const retagged = { ...draft, attrOverrides };
      return action.order && action.value !== null
        ? insertIntoOrder(retagged, action.attr, action.value, action.order)
        : retagged;
    }
  }
};

/** A new value placed in a list right after the last of `after` the list holds, first if none. */
const insertedAfter = (
  list: readonly string[],
  value: string,
  after: readonly string[],
): readonly string[] => {
  if (list.includes(value)) return list;
  const before = new Set(after);
  let anchor = -1;
  list.forEach((entry, index) => {
    if (before.has(entry)) anchor = index;
  });
  return [...list.slice(0, anchor + 1), value, ...list.slice(anchor + 1)];
};

/** A split-off brand joins the brand order where the base stands it: in the order for its level and
 *  in each scoped list of that level its old brand sits in. Without it the new brand would sort
 *  last. The scoped lists are keyed by the values above them and hold every level, so a list is
 *  read by its level, not by what it holds: a sub-brand list that says "Tide" under Tide is not a
 *  brand list. */
const insertIntoOrder = (
  draft: StudioDraft,
  attr: string,
  value: string,
  order: { from: string; after: readonly string[] },
): StudioDraft => {
  const levelOrder = draft.valueOrder[attr];
  const valueOrder = levelOrder
    ? { ...draft.valueOrder, [attr]: insertedAfter(levelOrder, value, order.after) }
    : draft.valueOrder;
  const listsLevel = (key: string): string | undefined => {
    const path = key === "" ? [] : key.split(PATH_SEPARATOR);
    return levelsForScope(draft.spine, draft.scopedLevelOrder, path)[path.length]?.attr;
  };
  let scopedValueOrder = draft.scopedValueOrder;
  for (const [key, list] of Object.entries(draft.scopedValueOrder)) {
    if (listsLevel(key) !== attr || !list.includes(order.from)) continue;
    const next = insertedAfter(list, value, order.after);
    if (next !== list) scopedValueOrder = { ...scopedValueOrder, [key]: next };
  }
  return { ...draft, valueOrder, scopedValueOrder };
};

export const applyFix = (draft: StudioDraft, flag: Flag): StudioDraft => {
  if (draft.appliedFixFlagIds.includes(flag.id)) return draft;
  const next = flag.fix ? applyFixAction(draft, flag.fix) : draft;
  return { ...next, appliedFixFlagIds: [...draft.appliedFixFlagIds, flag.id] };
};

/** Always record the exception; emit a restriction row only for a shipped CDT0 — a phantom MAY_HAVE empties the cell for every real variant. */
export const acceptFlag = (draft: StudioDraft, flag: Flag, cdt0?: Cdt0Resolution): StudioDraft => {
  if (isFlagAccepted(draft, flag.id)) return draft;
  const accepted = {
    ...draft,
    acceptedFlagIds: [...(draft.acceptedFlagIds ?? []), flag.id],
  };
  // CDT0 comes from the contract, not the path, so the row matches the assignments CSV.
  const variant = cdt0?.variantFor(flag.skuIds) ?? null;
  if (variant === null) return accepted;
  const kind: AcceptedRestriction["kind"] =
    flag.kind === "split" ? "split-off" : flag.kind === "orphan" ? "breakout" : "shelf-rule";
  const restriction: AcceptedRestriction = {
    id: `restriction:${flag.id}`,
    kind,
    cdtPath: [variant],
    collapsedKey: null,
    // Null is not "*": finalize measures, or the upload drops the row.
    placement: null,
    skuIds: flag.skuIds,
    source: { origin: "accepted-violation", flagId: flag.id },
    text: flag.title,
  };
  return { ...accepted, restrictions: [...accepted.restrictions, restriction] };
};

export const isFlagAccepted = (draft: StudioDraft, flagId: string): boolean =>
  (draft.acceptedFlagIds ?? []).includes(flagId) ||
  draft.restrictions.some((restriction) => restriction.source.flagId === flagId);

export const dismissFlag = (draft: StudioDraft, flagId: string): StudioDraft =>
  draft.dismissedFlagIds.includes(flagId)
    ? draft
    : { ...draft, dismissedFlagIds: [...draft.dismissedFlagIds, flagId] };

const SEVERITY_ORDER = { blocking: 0, toCheck: 1, toReview: 2 } as const;

export const resolveAll = (draft: StudioDraft, flags: readonly Flag[]): StudioDraft => {
  const open = flags
    .filter((flag) => !isFlagSettled(draft, flag.id))
    .sort((a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity]);
  let next = draft;
  for (const flag of open) {
    next = applyFix(next, flag);
  }
  return next;
};

export const isFlagSettled = (draft: StudioDraft, flagId: string): boolean =>
  (flagId.startsWith(SIZE_LAYOUT_PREFIX) &&
    draft.sizeLayoutAnswers?.[flagId.slice(SIZE_LAYOUT_PREFIX.length)] !== undefined) ||
  draft.appliedFixFlagIds.includes(flagId) ||
  draft.dismissedFlagIds.includes(flagId) ||
  isFlagAccepted(draft, flagId);

export const scopeKeyOf = (path: readonly string[]) => path.join(PATH_SEPARATOR);

/* ── answers that outlive the page (Wendy, 2026-10-23) ─────────────────────── */

/** The merchant's layout for a size-tier brand block the base lays out neither way, keyed
 *  "cdt0 › cdt1" as its question's scope. */
export const answerSizeLayout = (
  draft: StudioDraft,
  column: string,
  layout: SizeLayout,
): StudioDraft => ({
  ...draft,
  sizeLayoutAnswers: { ...draft.sizeLayoutAnswers, [column]: layout },
});

/** Change: the answer is taken back, and Studio asks again. */
export const clearSizeLayoutAnswer = (draft: StudioDraft, column: string): StudioDraft => {
  const { [column]: _taken, ...rest } = draft.sizeLayoutAnswers ?? {};
  return { ...draft, sizeLayoutAnswers: rest };
};

/** The size layout questions still waiting for an answer: Finalize waits for every one. */
export const unansweredSizeLayoutFlags = (flags: readonly Flag[], draft: StudioDraft): Flag[] =>
  flags.filter((flag) => flag.id.startsWith(SIZE_LAYOUT_PREFIX) && !isFlagSettled(draft, flag.id));

/** A size-tag Yes: the re-tag (`applyFix`), recorded so a reload can put it back. */
export const answerSizeTier = (draft: StudioDraft, flag: Flag, line: string): StudioDraft => {
  const applied = applyFix(draft, flag);
  if (flag.fix?.type !== "retagItems" || flag.fix.value === null) return applied;
  return {
    ...applied,
    sizeTierAnswers: {
      ...applied.sizeTierAnswers,
      [flag.id]: { skuIds: [...flag.fix.skuIds], to: flag.fix.value, line },
    },
  };
};

/** Change on a size-tag Yes kept from an earlier visit: its products lose the re-tag it gave. */
export const withdrawSizeTierAnswer = (draft: StudioDraft, flagId: string): StudioDraft => {
  const { [flagId]: answer, ...rest } = draft.sizeTierAnswers ?? {};
  if (!answer) return draft;
  const attrOverrides = { ...draft.attrOverrides };
  for (const skuId of answer.skuIds) {
    const own = attrOverrides[skuId];
    if (own?.[SIZE_TIER_ATTR] !== answer.to) continue;
    const { [SIZE_TIER_ATTR]: _tier, ...kept } = own;
    if (Object.keys(kept).length > 0) attrOverrides[skuId] = kept;
    else delete attrOverrides[skuId];
  }
  return {
    ...draft,
    attrOverrides,
    appliedFixFlagIds: draft.appliedFixFlagIds.filter((id) => id !== flagId),
    sizeTierAnswers: rest,
  };
};

/** What a reload keeps of the merchant's answers: every layout answer and every size-tag Yes. */
export interface KeptAnswers {
  sizeLayouts: Readonly<Record<string, SizeLayout>>;
  sizeTiers: Readonly<Record<string, SizeTierAnswer>>;
}

export const keptAnswersOf = (draft: StudioDraft): KeptAnswers => ({
  sizeLayouts: { ...draft.sizeLayoutAnswers },
  sizeTiers: { ...draft.sizeTierAnswers },
});

export const hasKeptAnswers = (kept: KeptAnswers | null): kept is KeptAnswers =>
  kept !== null &&
  (Object.keys(kept.sizeLayouts).length > 0 || Object.keys(kept.sizeTiers).length > 0);

/** A fresh draft (a reload, a re-inference) with the kept answers put back; the same draft when
 *  there is nothing to put back. An answer the draft already holds is not applied twice. */
export const withKeptAnswers = (draft: StudioDraft, kept: KeptAnswers | null): StudioDraft => {
  if (!hasKeptAnswers(kept)) return draft;
  const layouts = Object.entries(kept.sizeLayouts).filter(
    ([column]) => draft.sizeLayoutAnswers?.[column] === undefined,
  );
  const tiers = Object.entries(kept.sizeTiers).filter(
    ([flagId]) => !draft.appliedFixFlagIds.includes(flagId),
  );
  if (layouts.length === 0 && tiers.length === 0) return draft;
  let next: StudioDraft =
    layouts.length > 0
      ? {
          ...draft,
          sizeLayoutAnswers: { ...draft.sizeLayoutAnswers, ...Object.fromEntries(layouts) },
        }
      : draft;
  for (const [flagId, answer] of tiers) {
    const attrOverrides = { ...next.attrOverrides };
    for (const skuId of answer.skuIds)
      attrOverrides[skuId] = { ...attrOverrides[skuId], [SIZE_TIER_ATTR]: answer.to };
    next = {
      ...next,
      attrOverrides,
      appliedFixFlagIds: [...next.appliedFixFlagIds, flagId],
      sizeTierAnswers: { ...next.sizeTierAnswers, [flagId]: answer },
    };
  }
  return next;
};
